import { createReducer } from "reduxsauce"
import { find, first, isEmpty, omit } from "lodash"
import { takeLatest, select, put, all, takeEvery } from "redux-saga/effects"
import actionCreator from "helpers/actionCreator"
import { Types as LoaderTypes } from "model/data"
import { Types as GraphqlLoaderTypes } from "model/graphql"
import { Types as BaseTypes } from "modules/portfolio-root/model"
import { getCategoryItemsSubset, getOptions } from "./selectors/data"
import { getDisplayType } from "./selectors/items"

export const { Types, Creators } = actionCreator("ompnt/modules/analyzeFactorSensitivity", {
  toggle: {
    props: ["id", "currentState", "factorSensitivityType"],
    track: true,
    trackType: "analyze-factor-sensitivity-graph-legend-factor",
    trackPayload: ({ id, currentState, factorSensitivityType }) => ({ factor: id, state: currentState ? "on" : "off", factorSensitivityType }),
  },
  focusChartLine: ["focusedChartLine"],
  blurChartLine: [],
  setDisplayType: {
    props: ["displayType", "factorSensitivityType"],
    track: true,
    trackType: "analyze-factor-sensitivity-category-selection",
    trackPayload: ({ displayType, factorSensitivityType }) => ({ type: displayType, factorSensitivityType }),
  },
  trackCSVDownload: {
    track: true,
    trackType: "analyze-factor-sensitivity-graph-timeseries-download",
  },
  toggleOnly: {
    props: ["id", "value", "factorSensitivityType"],
    track: true,
    trackType: "analyze-factor-sensitivity-graph-legend-factor",
    trackPayload: ({ id, factorSensitivityType }) => ({ factor: id, factorSensitivityType }),
  },
  toggleOnlySuccess: ["selected", "only", "items"],
})

const DEFAULT_STATE = {
  displayType: "style",
  focusedChartLine: null,
  contributorsDetailsVisible: false,
  selectedContributor: null,
  disabled: {},
  only: null,
}

function handleSetDisplayType(state, { displayType }) {
  return { ...state, displayType, only: null }
}

function handleToggle(state, { id }) {
  let disabled
  let focusedChartLine

  if (state.disabled[id]) {
    focusedChartLine = id
    disabled = omit(state.disabled, id)
  } else {
    focusedChartLine = null
    disabled = { [id]: true, ...state.disabled }
  }
  return { ...state, disabled, focusedChartLine, only: null }
}

function handleFocusChartLine(state, { focusedChartLine }) {
  return { ...state, focusedChartLine }
}

function handleBlurChartLine(state) {
  return { ...state, focusedChartLine: null }
}

function handleToggleOnlySuccess(state, { selected, only, items }) {
  const disabled = {}
  let focusedChartLine = null
  let onlyDisplay = null

  if (only) {
    focusedChartLine = selected
    onlyDisplay = selected
    items.forEach(item => {
      if (item.id !== selected) {
        disabled[item.id] = true
      }
    })
  }

  return { ...state, disabled, focusedChartLine, only: onlyDisplay }
}

function handleSetModel(state) {
  return { ...state, only: null, focusedChartLine: null }
}

function handleLoadSuccess(state, action) {
  if (action.resource === "portfolio") {
    return DEFAULT_STATE
  }
  return state
}

export default createReducer(DEFAULT_STATE, {
  [Types.TOGGLE]: handleToggle,
  [Types.FOCUS_CHART_LINE]: handleFocusChartLine,
  [Types.BLUR_CHART_LINE]: handleBlurChartLine,
  [Types.SET_DISPLAY_TYPE]: handleSetDisplayType,
  [Types.TOGGLE_ONLY_SUCCESS]: handleToggleOnlySuccess,
  [BaseTypes.SET_MODEL]: handleSetModel,
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
})

function* handleToggleOnly({ id }) {
  const { items, only } = yield select(state => ({
    items: getCategoryItemsSubset(state, { isStyleFactorsAdditive: false }),
    only: state.modules.analyzeFactorSensitivity.only,
  }))

  yield put(Creators.toggleOnlySuccess(id, only !== id, items))
}

function* handleGraphqlLoadSuccess({ resource }) {
  if (resource === "factorSensitivity") {
    const [options, displayType] = yield all([
      select(getOptions),
      select(getDisplayType),
    ])

    if (!isEmpty(options) && !find(options, { id: displayType })) {
      yield put(Creators.setDisplayType(first(options).id))
    }
  }
}

export function* saga() {
  yield takeLatest(Types.TOGGLE_ONLY, handleToggleOnly)
  yield takeEvery(GraphqlLoaderTypes.LOAD_SUCCESS, handleGraphqlLoadSuccess)
}
